/**
 * 评价域 API(FR-10)。提交评价 + 评价图片直传对象存储凭证。
 * 直传语义对齐 rn-app `uploadToBucket`:mock 实装下 `uploadUrl === finalUrl` → 跳过 PUT;
 * 真实 MinIO 下 presigned PUT URL(签名嵌 query)→ PUT 文件字节。Web 用浏览器原生 File。
 */
import { call } from './client';
import type { components } from './openapi.generated';

type Schemas = components['schemas'];
export type ReviewSubmitRequest = Schemas['ReviewSubmitRequest'];
export type ReviewImageUploadCredential = Schemas['ReviewImageUploadCredentialResponse'];

/** 评价业务错误码(语义引 product-spec review.md / 后端)。 */
export const REVIEW_ERROR = {
  ALREADY_REVIEWED: '030601',
  CONTENT_REJECTED: '030602',
  WINDOW_EXPIRED: '030603',
  RATING_INVALID: '030604',
  CONTENT_LENGTH: '030605',
  IMAGE_COUNT: '030606',
  ORDER_NOT_FOUND: '030301',
  ORDER_NOT_OWNED: '030302',
} as const;

/** 签发评价图片直传凭证(选图前调用,身份态 ≥ REGISTERED)。 */
export function getReviewUploadCredential(orderId: string): Promise<ReviewImageUploadCredential> {
  return call<ReviewImageUploadCredential>({
    method: 'GET',
    url: '/api/v1/reviews/upload-credential',
    params: { orderId },
  });
}

/**
 * 上传一张评价图片到对象存储,返回 finalUrl(写入 review.images)。
 * mock(uploadUrl === finalUrl)直接返回 finalUrl,不发 PUT(dev/测试 0 改动)。
 */
export async function uploadReviewImage(orderId: string, file: File): Promise<string> {
  const cred = await getReviewUploadCredential(orderId);
  if (!cred.uploadUrl || !cred.finalUrl) {
    throw new Error('上传凭证无效');
  }
  if (cred.uploadUrl === cred.finalUrl) {
    return cred.finalUrl; // mock 实装:无需真实 PUT
  }
  const resp = await fetch(cred.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'image/jpeg' },
  });
  if (!resp.ok) {
    throw new Error(`图片上传失败:${resp.status}`);
  }
  return cred.finalUrl;
}

/** 提交评价(FR-C8 提交即终态不可改)。 */
export function submitReview(req: ReviewSubmitRequest): Promise<Schemas['ReviewSubmitResponse']> {
  return call<Schemas['ReviewSubmitResponse']>({
    method: 'POST',
    url: '/api/v1/reviews',
    data: req,
  });
}
